import { cloneDeep, isEqual, mergeWith, sortBy } from 'lodash';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { WatchList } from '../../models/WatchList';
import { ApiError } from '../../services/api-error.model';
import { UpdateWatchListDto } from '../../services/dtos/watchLists/updateWatchList.dto';
import { updateWatchList } from '../../services/watchLists/watchLists.service';
import { Channels } from '../../utils/channels';
import { queryKeys } from '../queryKeyFactory';
import { invalidateFeedQueries } from '../../utils/invalidateFeedQueries';
import useMutationErrorToast from '../toast/useMutationErrorToast';
import { mapWatchListsDtoToWatchLists } from './utils/watchLists.mappers';

export interface UpdateWatchListPayload {
  id: string;
  watchList: UpdateWatchListDto;
}

export default (): UseMutationResult<
  WatchList,
  ApiError,
  UpdateWatchListPayload,
  { previousWatchLists: WatchList[] }
> => {
  const queryClient = useQueryClient();
  const { showErrorToast } = useMutationErrorToast();

  return useMutation(
    async ({ id, watchList }: UpdateWatchListPayload) => {
      const updatedWatchlist = await updateWatchList(id, watchList);

      return mapWatchListsDtoToWatchLists([updatedWatchlist])[0];
    },
    {
      onMutate: async ({ id, watchList }) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries([queryKeys.fetchWatchlists.all]);

        const previousWatchLists = queryClient.getQueryData<WatchList[]>([queryKeys.fetchWatchlists.all])!;

        const updatedWatchLists = cloneDeep(previousWatchLists);

        const updatedWatchList = updatedWatchLists.find((previousWatchlistId) => previousWatchlistId.id === id)!;

        mergeWith(updatedWatchList, watchList, (objValue, srcValue) => srcValue);
        updatedWatchList.updatedAt = new Date().toISOString();

        queryClient.setQueryData([queryKeys.fetchWatchlists.all], updatedWatchLists);

        return { previousWatchLists };
      },
      onError: (error, _updateDto, context) => {
        queryClient.setQueryData([queryKeys.fetchWatchlists.all], context?.previousWatchLists);
        showErrorToast(error);
      },
      onSuccess: (watchlist, _, context) => {
        const previousWatchList = context?.previousWatchLists.find(
          (previousWatchlistId) => previousWatchlistId.id === watchlist.id
        )!;
        const hasIssuersChanged = !isEqual(sortBy(previousWatchList.tags), sortBy(watchlist.tags));
        const hasMyCSChanged = previousWatchList.myCS !== watchlist.myCS;

        if (hasIssuersChanged) {
          queryClient.invalidateQueries(queryKeys.fetchIssuers.query({ watchlistId: watchlist?.id }));
        }

        if ((hasIssuersChanged && watchlist.myCS) || hasMyCSChanged) {
          invalidateFeedQueries(queryClient);
        }
        Channels.watchlistsChannel.postMessage(watchlist.id);
      },
    }
  );
};
